import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import {
  ArrowLeft, Calendar, Users, Plus, Edit2, Trash2, Upload,
  ExternalLink, UploadCloud, FolderPlus, AlertTriangle, Clock, Film,
  Square, CheckSquare, X, CheckCircle,
} from 'lucide-react';
import CharacterCard from '../components/projects/CharacterCard';
import FilesSection from '../components/projects/FilesSection';
import {
  AddCharactersModal, ModifyCharactersModal, EditCharacterModal, ReplaceRequestModal,
} from '../components/projects/ProjectModals';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProjectDetails = () => {
  const { projectId } = useParams();
  const { getAuthHeaders, user } = useAuth();
  const confirm = useConfirm();
  const [project, setProject] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [files, setFiles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCharacters, setShowAddCharacters] = useState(false);
  const [showModifyCharacters, setShowModifyCharacters] = useState(false);
  const [showEditCharacter, setShowEditCharacter] = useState(null);
  const [charactersText, setCharactersText] = useState('');
  const [modifyText, setModifyText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedChars, setSelectedChars] = useState(new Set());
  const [driveConnected, setDriveConnected] = useState(null);
  const [replacementRequests, setReplacementRequests] = useState([]);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceMotivo, setReplaceMotivo] = useState('');

  const isAdmin = user?.role === 'dono' || user?.role === 'administrador';
  const isDublador = user?.role === 'dublador';

  const myAssignedChars = characters.filter(c =>
    c.assigned_user_id === user?.id ||
    (c.assigned_users || []).some(u => u.id === user?.id)
  );
  const myOpenRequest = replacementRequests.find(r =>
    r.status === 'aberto' && r.requester_id === user?.id
  );

  useEffect(() => {
    fetchProject();
    if (isAdmin) {
      fetchUsers();
      checkDriveStatus();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, { headers: getAuthHeaders() });
      setProject(response.data.project);
      setCharacters(response.data.characters || []);
      setFiles(response.data.files || []);
      setReplacementRequests(response.data.replacement_requests || []);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Erro ao carregar projeto');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReplacement = async () => {
    try {
      const fd = new FormData();
      fd.append('project_id', projectId);
      fd.append('motivo', replaceMotivo || '');
      await axios.post(`${API}/replacement-requests`, fd, { headers: getAuthHeaders() });
      toast.success('Solicitação enviada. Os administradores foram avisados.');
      setShowReplaceModal(false);
      setReplaceMotivo('');
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar substituição');
    }
  };

  const handleResolveReplacement = async (reqId) => {
    try {
      await axios.put(`${API}/replacement-requests/${reqId}/resolve`, null, { headers: getAuthHeaders() });
      toast.success('Solicitação marcada como resolvida.');
      fetchProject();
    } catch {
      toast.error('Erro ao resolver solicitação');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders() });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const checkDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/drive/status`, { headers: getAuthHeaders() });
      setDriveConnected(response.data.connected);
    } catch { setDriveConnected(false); }
  };

  const handleConnectDrive = async () => {
    try {
      const response = await axios.get(`${API}/drive/connect`, { headers: getAuthHeaders() });
      window.open(response.data.authorization_url, '_blank', 'width=600,height=700');
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/drive/status`, { headers: getAuthHeaders() });
          if (statusRes.data.connected) {
            clearInterval(pollInterval);
            setDriveConnected(true);
            toast.success('Google Drive conectado com sucesso!');
          }
        } catch (err) { /* polling silently */ }
      }, 3000);
      setTimeout(() => clearInterval(pollInterval), 120000);
    } catch (error) {
      toast.error('Erro ao conectar ao Google Drive');
    }
  };

  const handleAddCharacters = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('characters_text', charactersText);
      await axios.post(`${API}/projects/${projectId}/characters`, formData, { headers: getAuthHeaders() });
      toast.success('Personagens adicionados!');
      setShowAddCharacters(false);
      setCharactersText('');
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar personagens');
    }
  };

  const handleModifyCharacters = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('characters_text', modifyText);
      const res = await axios.post(`${API}/projects/${projectId}/characters/bulk-modify`, formData, { headers: getAuthHeaders() });
      const data = res.data;
      let msg = `${data.modified} personagem(ns) modificado(s)`;
      if (data.not_found?.length > 0) msg += ` | Não encontrados: ${data.not_found.join(', ')}`;
      toast.success(msg);
      setShowModifyCharacters(false);
      setModifyText('');
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao modificar personagens');
    }
  };

  const handleUpdateCharacter = async (characterId) => {
    try {
      const formData = new FormData();
      if (selectedUserId !== undefined) {
        formData.append('assigned_user_id', selectedUserId);
      }
      await axios.put(`${API}/projects/${projectId}/characters/${characterId}`, formData, { headers: getAuthHeaders() });
      toast.success('Personagem atualizado!');
      setShowEditCharacter(null);
      setSelectedUserId('');
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar personagem');
    }
  };

  const handleDeleteCharacter = async (characterId) => {
    const ok = await confirm('Excluir este personagem?');
    if (!ok) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/characters/${characterId}`, { headers: getAuthHeaders() });
      toast.success('Personagem excluído!');
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir personagem');
    }
  };

  const handleStatusChange = async (characterId, newStatus, targetUserId = null) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      if (targetUserId) formData.append('target_user_id', targetUserId);
      await axios.put(`${API}/projects/${projectId}/characters/${characterId}/status`, formData, { headers: getAuthHeaders() });
      toast.success('Status atualizado!');
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);

    const CHUNK_SIZE = 512 * 1024;
    const useChunked = file.size > CHUNK_SIZE;

    try {
      if (useChunked) {
        const initForm = new FormData();
        initForm.append('file_name', file.name);
        initForm.append('file_size', file.size);
        initForm.append('file_type', file.type || 'application/octet-stream');
        const initRes = await axios.post(`${API}/projects/${projectId}/files/init-upload`, initForm, { headers: getAuthHeaders() });
        const uploadId = initRes.data.upload_id;

        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunkBlob = file.slice(start, end);
          const chunkForm = new FormData();
          chunkForm.append('upload_id', uploadId);
          chunkForm.append('chunk_index', i);
          chunkForm.append('chunk', chunkBlob, `chunk_${i}`);
          await axios.post(`${API}/projects/${projectId}/files/upload-chunk`, chunkForm, { headers: getAuthHeaders() });
          setUploadProgress(Math.round(((i + 1) / totalChunks) * 90));
        }

        const completeForm = new FormData();
        completeForm.append('upload_id', uploadId);
        const response = await axios.post(`${API}/projects/${projectId}/files/complete-upload`, completeForm, { headers: getAuthHeaders() });
        setUploadProgress(100);
        const driveStatus = response.data?.drive_status;
        if (driveStatus === 'uploaded') toast.success('Arquivo enviado e salvo no Google Drive!');
        else if (driveStatus === 'failed') toast.warning('Arquivo salvo localmente, mas falhou ao enviar para o Google Drive.');
        else toast.success('Arquivo enviado!');
      } else {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/projects/${projectId}/files`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded / p.total) * 100)),
        });
        const driveStatus = response.data?.drive_status;
        if (driveStatus === 'uploaded') toast.success('Arquivo enviado e salvo no Google Drive!');
        else if (driveStatus === 'failed') toast.warning('Arquivo salvo localmente, mas falhou ao enviar para o Google Drive.');
        else toast.success('Arquivo enviado!');
      }
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const canUpload = () => {
    if (isAdmin) return true;
    return characters.some(c => c.assigned_user_id === user?.id);
  };

  const handleCreateDriveFolder = async () => {
    setCreatingFolder(true);
    try {
      await axios.post(`${API}/projects/${projectId}/create-drive-folder`, {}, { headers: getAuthHeaders() });
      toast.success('Pasta criada no Google Drive!');
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pasta no Drive');
    } finally {
      setCreatingFolder(false);
    }
  };

  const canChangeStatus = (character) => {
    if (isAdmin) return true;
    if (character.assigned_user_id === user?.id) return true;
    if (character.assigned_users?.some(u => u.id === user?.id)) return true;
    return false;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const parts = dateStr.split('T')[0].split('-');
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    } catch { return dateStr; }
  };

  const filteredUsers = allUsers.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.artist_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const toggleCharSelect = (charId) => {
    setSelectedChars(prev => {
      const next = new Set(prev);
      next.has(charId) ? next.delete(charId) : next.add(charId);
      return next;
    });
  };

  const bulkDeliver = async () => {
    const ok = await confirm(`Marcar ${selectedChars.size} personagem(ns) como ENTREGUE?`);
    if (!ok) return;
    try {
      for (const cid of selectedChars) {
        const formData = new FormData();
        formData.append('status', 'ENTREGUE');
        await axios.put(`${API}/projects/${projectId}/characters/${cid}/status`, formData, { headers: getAuthHeaders() });
      }
      toast.success(`${selectedChars.size} personagens marcados como entregue!`);
      setSelectedChars(new Set());
      fetchProject();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const bulkDelete = async () => {
    const ok = await confirm(`Excluir ${selectedChars.size} personagem(ns)?`);
    if (!ok) return;
    try {
      for (const cid of selectedChars) {
        await axios.delete(`${API}/projects/${projectId}/characters/${cid}`, { headers: getAuthHeaders() });
      }
      toast.success(`${selectedChars.size} personagens excluídos!`);
      setSelectedChars(new Set());
      fetchProject();
    } catch { toast.error('Erro ao excluir'); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="p-10 text-center">
          <p className="text-slate-400">Projeto não encontrado.</p>
          <Link to="/projects" className="text-teal-400 hover:underline mt-4 inline-block">
            Voltar para Projetos
          </Link>
        </div>
      </Layout>
    );
  }

  // Sort: user's own characters first
  const sortedCharacters = [...characters].sort((a, b) => {
    const aIsMine = a.assigned_user_id === user?.id || (a.assigned_users || []).some(u => u.id === user?.id);
    const bIsMine = b.assigned_user_id === user?.id || (b.assigned_users || []).some(u => u.id === user?.id);
    if (aIsMine && !bIsMine) return -1;
    if (!aIsMine && bIsMine) return 1;
    return 0;
  });

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="project-details">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/projects" className="bg-slate-700 hover:bg-slate-600 p-2 rounded-lg transition-colors">
              <ArrowLeft className="h-5 w-5 text-white" />
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">{project.title}</h1>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">{project.project_type}</span>
                {project.client && <span className="text-slate-400 text-sm">Cliente: {project.client}</span>}
                {project.responsible_admin_name && (
                  <span className="text-teal-400 text-sm">Responsável: {project.responsible_admin_name}</span>
                )}
              </div>
            </div>
          </div>

          {canUpload() && (
            <label className={`flex items-center gap-2 ${project.drive_upload_folder_link ? 'bg-blue-500 hover:bg-blue-600' : 'bg-teal-500 hover:bg-teal-600'} text-white px-4 py-2 rounded-lg transition-colors cursor-pointer`} data-testid="upload-file-btn">
              {uploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
              ) : project.drive_upload_folder_link ? (
                <UploadCloud className="h-5 w-5" />
              ) : (
                <Upload className="h-5 w-5" />
              )}
              {uploading ? `Enviando... ${uploadProgress}%` : project.drive_upload_folder_link ? 'Enviar para Drive' : 'Enviar Arquivo'}
              <input
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
                data-testid="file-upload-input"
              />
            </label>
          )}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <InfoCard icon={<Calendar />} label="Prazo de Entrega" value={formatDate(project.delivery_deadline)} />
          <InfoCard icon={<Calendar />} label="Prazo da Demo" value={formatDate(project.demo_deadline)} />
          {project.duration_display && (
            <InfoCard icon={<Clock />} label="Duração" value={project.duration_display} />
          )}
          {project.episodes > 0 && (
            <InfoCard icon={<Film />} label="Episódios" value={String(project.episodes)} />
          )}
          {!isDublador && project.final_deadline && (
            <InfoCard icon={<Calendar />} label="Data Final" value={formatDate(project.final_deadline)} highlight />
          )}
          {project.drive_folder_link ? (
            <a
              href={project.drive_folder_link}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center gap-3 hover:bg-blue-500/20 transition-colors"
              data-testid="drive-folder-link"
            >
              <ExternalLink className="h-5 w-5 text-blue-400" />
              <span className="text-blue-400 font-medium">Material do Projeto</span>
            </a>
          ) : isAdmin && (
            <button
              onClick={handleCreateDriveFolder}
              disabled={creatingFolder}
              className="bg-blue-500/10 border border-blue-500/30 border-dashed rounded-lg p-4 flex items-center gap-3 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
              data-testid="create-drive-folder-btn"
            >
              {creatingFolder ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-blue-400"></div>
              ) : (
                <FolderPlus className="h-5 w-5 text-blue-400" />
              )}
              <span className="text-blue-400 font-medium">{creatingFolder ? 'Criando...' : 'Criar Pasta no Drive'}</span>
            </button>
          )}

          {project.drive_upload_folder_link && (
            <a
              href={project.drive_upload_folder_link}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4 flex items-center gap-3 hover:bg-teal-500/20 transition-colors"
              data-testid="drive-upload-folder-link"
            >
              <UploadCloud className="h-5 w-5 text-teal-400" />
              <span className="text-teal-400 font-medium">Pasta de Destino</span>
            </a>
          )}
        </div>

        {/* Replacement Requests — persistent alerts */}
        {replacementRequests.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-5 space-y-3" data-testid="replacement-alerts">
            <h3 className="text-red-300 font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Solicitação(ões) de substituição pendente(s) ({replacementRequests.length})
            </h3>
            {replacementRequests.map(r => (
              <div key={r.id} className="bg-slate-900/60 border border-red-500/30 rounded-lg px-4 py-3 flex items-start gap-3" data-testid={`replacement-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white">
                    <span className="font-semibold">{r.requester_name}</span> solicitou substituição em:
                  </p>
                  <p className="text-sm text-slate-300 mt-0.5">
                    {(r.character_names || []).join(', ')}
                  </p>
                  {r.motivo && <p className="text-xs text-slate-400 mt-1 italic">"{r.motivo}"</p>}
                  <p className="text-[10px] text-slate-500 mt-1">
                    {new Date(r.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => handleResolveReplacement(r.id)}
                    className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                    data-testid={`resolve-replacement-${r.id}`}
                  >
                    Marcar como resolvida
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Dublador → Solicitar substituição button */}
        {isDublador && myAssignedChars.length > 0 && !myOpenRequest && (
          <button
            onClick={() => setShowReplaceModal(true)}
            className="w-full bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition-colors"
            data-testid="request-replacement-btn"
          >
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">Solicitar Substituição</span>
            <span className="text-xs opacity-70">
              ({myAssignedChars.length} personagem{myAssignedChars.length !== 1 ? 's' : ''})
            </span>
          </button>
        )}
        {isDublador && myOpenRequest && (
          <div className="bg-amber-500/5 border border-amber-500/30 text-amber-300 rounded-xl px-4 py-3 text-sm" data-testid="replacement-pending-self">
            Sua solicitação de substituição está aguardando um administrador.
          </div>
        )}

        {/* Characters Section */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2" data-testid="chars-heading">
              <Users className="h-5 w-5 text-teal-400" />
              Personagens ({characters.filter(c => c.status === 'ENTREGUE').length}/{characters.length})
            </h2>
            <div className="flex gap-2">
              {isAdmin && characters.length > 0 && (
                <button
                  onClick={() => {
                    if (selectedChars.size === characters.length) setSelectedChars(new Set());
                    else setSelectedChars(new Set(characters.map(c => c.id)));
                  }}
                  className="flex items-center gap-1.5 text-slate-400 hover:text-white text-xs px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors"
                  data-testid="select-all-chars"
                >
                  {selectedChars.size === characters.length ? <CheckSquare className="h-3.5 w-3.5 text-teal-400" /> : <Square className="h-3.5 w-3.5" />}
                  {selectedChars.size === characters.length ? 'Desmarcar' : 'Todos'}
                </button>
              )}
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowAddCharacters(true)}
                    className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                    data-testid="add-characters-btn"
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar
                  </button>
                  <button
                    onClick={() => setShowModifyCharacters(true)}
                    className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                    data-testid="modify-characters-btn"
                  >
                    <Edit2 className="h-4 w-4" />
                    Modificar
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Bulk Action Bar */}
          {isAdmin && selectedChars.size > 0 && (
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg px-4 py-2 flex items-center justify-between mb-4" data-testid="char-bulk-bar">
              <span className="text-teal-400 font-medium text-sm">{selectedChars.size} selecionado(s)</span>
              <div className="flex gap-2">
                <button
                  onClick={bulkDeliver}
                  className="flex items-center gap-1.5 px-3 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium hover:bg-green-500/30 transition-colors"
                  data-testid="bulk-deliver-chars"
                >
                  <CheckCircle className="h-3 w-3" /> Entregue
                </button>
                <button
                  onClick={bulkDelete}
                  className="flex items-center gap-1.5 px-3 py-1 bg-red-500/20 text-red-400 rounded text-xs font-medium hover:bg-red-500/30 transition-colors"
                  data-testid="bulk-delete-chars"
                >
                  <Trash2 className="h-3 w-3" /> Excluir
                </button>
                <button onClick={() => setSelectedChars(new Set())} className="p-1 text-slate-400 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Characters Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedCharacters.map(character => (
              <CharacterCard
                key={character.id}
                character={character}
                isAdmin={isAdmin}
                user={user}
                allUsers={allUsers}
                projectId={projectId}
                selected={selectedChars.has(character.id)}
                onToggleSelect={() => toggleCharSelect(character.id)}
                onRefresh={fetchProject}
                onEdit={(ch) => {
                  setShowEditCharacter(ch);
                  setSelectedUserId(ch.assigned_user_id || '');
                }}
                onDelete={handleDeleteCharacter}
                onStatusChange={handleStatusChange}
                canChangeStatus={canChangeStatus}
                getAuthHeaders={getAuthHeaders}
              />
            ))}
          </div>

          {characters.length === 0 && (
            <p className="text-slate-400 text-center py-8">Nenhum personagem cadastrado.</p>
          )}
        </div>

        {/* Files Section */}
        <FilesSection
          project={project}
          files={files}
          isAdmin={isAdmin}
          projectId={projectId}
          driveConnected={driveConnected}
          getAuthHeaders={getAuthHeaders}
          onRefresh={fetchProject}
          onConnectDrive={handleConnectDrive}
          confirm={confirm}
        />

        {/* Modals */}
        <AddCharactersModal
          open={showAddCharacters}
          onClose={() => setShowAddCharacters(false)}
          charactersText={charactersText}
          setCharactersText={setCharactersText}
          onSubmit={handleAddCharacters}
        />
        <ModifyCharactersModal
          open={showModifyCharacters}
          onClose={() => setShowModifyCharacters(false)}
          modifyText={modifyText}
          setModifyText={setModifyText}
          onSubmit={handleModifyCharacters}
        />
        <EditCharacterModal
          character={showEditCharacter}
          onClose={() => { setShowEditCharacter(null); setUserSearch(''); }}
          userSearch={userSearch}
          setUserSearch={setUserSearch}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          filteredUsers={filteredUsers}
          onSave={() => handleUpdateCharacter(showEditCharacter?.id)}
        />
        <ReplaceRequestModal
          open={showReplaceModal}
          onClose={() => setShowReplaceModal(false)}
          myAssignedChars={myAssignedChars}
          replaceMotivo={replaceMotivo}
          setReplaceMotivo={setReplaceMotivo}
          onSubmit={handleRequestReplacement}
        />
      </div>
    </Layout>
  );
};

const InfoCard = ({ icon, label, value, highlight }) => (
  <div className={`rounded-lg p-4 ${highlight ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-slate-700/50'}`}>
    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
      {icon && <span className="h-4 w-4">{icon}</span>}
      {label}
    </div>
    <p className={`font-medium ${highlight ? 'text-purple-400' : 'text-white'}`}>{value}</p>
  </div>
);

export default ProjectDetails;
